import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import deliveryService from '../../../services/deliveryService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import { buildToggleAction, useCanUpdate } from '../../common/activeToggle';
import { useDeliveryCompanies } from '../../../hooks/useDeliveryCompanies';

const EMPTY_FORM = { name: '', code: '', sortOrder: '' };

/** Delivery companies (Zr, Yalidine, ...): add, rename, reorder, activate / deactivate. Never deleted. */
const DeliveryCompaniesList = () => {
  const { t } = useTranslation();
  const canEdit = useCanUpdate('stores.update');
  const { companies, loading, reload } = useDeliveryCompanies();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the company being edited, null = creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (company) => {
    setEditing(company);
    setForm({ name: company.name, code: company.code, sortOrder: String(company.sortOrder ?? '') });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) return;
    const sortOrder = form.sortOrder === '' ? undefined : Number(form.sortOrder);
    setSaving(true);
    try {
      if (editing) {
        await deliveryService.updateCompany(editing.id, { name, sortOrder, active: editing.active });
      } else {
        await deliveryService.createCompany({ name, code: form.code.trim() || undefined, sortOrder });
      }
      toast.success(t('delivery.companiesPage.saveSuccess'));
      setDialogOpen(false);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (company) => {
    try {
      await deliveryService.setCompanyActive(company.id, !company.active);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    }
  };

  const columns = [
    { field: 'sortOrder', headerName: t('delivery.companiesPage.sortOrder'), width: 110 },
    { field: 'name', headerName: t('delivery.companiesPage.name'), flex: 1, minWidth: 180 },
    { field: 'code', headerName: t('delivery.companiesPage.code'), width: 180 },
    {
      field: 'active',
      headerName: t('common.status'),
      width: 130,
      valueFormatter: (p) => (p.value ? t('common.active') : t('common.inactive')),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 120,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              hidden: !canEdit,
              onClick: () => openEdit(params.data),
            },
            buildToggleAction({
              // buildToggleAction reads `isActive`; the company field is `active`.
              row: { ...params.data, isActive: params.data.active },
              onToggle: () => handleToggle(params.data),
              t,
              canToggle: canEdit,
            }),
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('delivery.companiesPage.title')}</Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('delivery.companiesPage.add')}
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('delivery.companiesPage.help')}
          </Typography>
          <AppDataGrid
            rows={companies.map((c) => ({ ...c, isActive: c.active }))}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {editing ? t('delivery.companiesPage.editTitle') : t('delivery.companiesPage.addTitle')}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3 }}>
          <TextField
            autoFocus
            fullWidth
            required
            label={t('delivery.companiesPage.name')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label={t('delivery.companiesPage.code')}
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            disabled={Boolean(editing)}
            helperText={editing ? t('delivery.companiesPage.codeLocked') : t('delivery.companiesPage.codeHelp')}
            inputProps={{ maxLength: 30 }}
          />
          <TextField
            fullWidth
            type="number"
            label={t('delivery.companiesPage.sortOrder')}
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            helperText={t('delivery.companiesPage.sortHelp')}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.name.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryCompaniesList;
